import React, { useEffect, useState } from "react";
import Button from "../../../ui/Button";
import ModalInput from "../../../ui/ModalTextInput";
import Dropdown from "../../../ui/SelectDropdown";
import CustomModalContainer from "../../../../components/modals/CustomModalContainer";
import { useAppDispatch, useAppSelector } from "../../../../redux/app/hooks";
import { mapDataToLabelValue } from "../../../../helpers";
import { fetchCourierOffice } from "../../../../redux/features/CourrierSlice";
import { createAdminAsync } from "../../../../redux/features/adminSlice";
import {
  AddAdminFormData,
  AddAdminModalProps,
} from "../../admin-management/types";
import { addAdminValidationSchema } from "../../admin-management/schemas";
import { RootState } from "../../../../redux/app/store";
import { useParams } from "react-router-dom";

const AddAgentModal: React.FC<AddAdminModalProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<AddAdminFormData>({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    gender: "",
    address: "",
    role: "COURIER_AGENT",
    offices: "",
    username: "",
    password: "",
    confirmPassword: "",
    country: "",
  });

  const [errors, setErrors] = useState<any>({});
  const { countries } = useAppSelector((state: RootState) => state.util);
  const { courierOffices } = useAppSelector(
    (state: RootState) => state.courier
  );
  const { isLoading } = useAppSelector((state: RootState) => state.admin);
  const dispatch = useAppDispatch();
  const { id } = useParams();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear the error for this field when the user types
    setErrors((prevErrors: any) => ({
      ...prevErrors,
      [name]: "", // Clear error for the specific input
    }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData({ ...formData, [name]: value });

    // Clear the error for this field when an option is selected
    setErrors((prevErrors: any) => ({
      ...prevErrors,
      [name]: "", // Clear error for the specific select field
    }));
  };

  const handleDiscard = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      gender: "",
      address: "",
      role: "COURIER_AGENT",
      offices: "",
      username: "",
      password: "",
      confirmPassword: "",
      country: "",
    });
    onClose();
    setErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Validate form using Yup
      await addAdminValidationSchema.validate(formData, { abortEarly: false });

      dispatch(
        createAdminAsync({
          firstName: formData?.firstName,
          lastName: formData?.lastName,
          email: formData?.email,
          phoneNumber: formData?.phoneNumber,
          gender: formData?.gender,
          address: formData?.address,
          role: "COURIER_AGENT",
          courierOfficeId:
            formData.role === "COURIER_ADMIN" ||
            formData.role === "COURIER_AGENT"
              ? formData?.offices
              : "",
          username: formData?.username,
          password: formData?.password,
        })
      )
        .unwrap()
        .then(() => {
          handleDiscard();
          onSave();
        })
        .catch((error) => {
          console.error("Failed to create admin:", error);
          // Optionally handle error (e.g., show a notification or error message)
        });
    } catch (err: any) {
      // Handle validation errors
      const validationErrors: any = {};
      err.inner.forEach((error: any) => {
        validationErrors[error.path] = error.message;
      });
      setErrors(validationErrors); // Set validation errors in state
    }
  };

  useEffect(() => {
    dispatch(
      fetchCourierOffice({ courierId: id, countryId: formData.country })
    );
  }, [formData.country, dispatch, id]);

  if (!open) return null;

  return (
    <CustomModalContainer
      title="Add Agent"
      footer={
        <div className="flex justify-between relative bottom-0">
          <Button
            type="button"
            onClick={handleDiscard}
            label="Discard"
            layout="inline" variant="ghost"
          />
          <Button
            label="Done"
            type="submit"
            layout="inline"
            onClick={handleSubmit}
            disabled={isLoading}
            isLoading={isLoading}
          />
        </div>
      }
      open={open}
      onClose={handleDiscard}
    >
      <div className="p-6">
        <form onSubmit={handleSubmit}>
          <ModalInput
            label="First Name"
            name="firstName"
            placeholder="Enter first name"
            value={formData.firstName}
            onChange={handleInputChange}
            required
            error={errors.firstName}
          />

          <ModalInput
            label="Last Name"
            name="lastName"
            placeholder="Enter last name"
            value={formData.lastName}
            onChange={handleInputChange}
            required
            error={errors.lastName}
          />

          <ModalInput
            label="Email Address"
            name="email"
            type="email"
            placeholder="Enter email address"
            value={formData.email}
            onChange={handleInputChange}
            required
            error={errors.email}
          />

          <ModalInput
            label="Phone Number"
            name="phoneNumber"
            type="tel"
            placeholder="Enter phone number"
            value={formData.phoneNumber}
            onChange={handleInputChange}
            required
            error={errors.phoneNumber}
          />

          <Dropdown
            label="Gender"
            name="gender"
            options={["Male", "Female", "Other"]}
            required={true}
            value={formData.gender}
            onSelect={(value) => handleSelectChange("gender", value)}
            error={errors.gender}
            placeholder="Select gender"
          />

          <ModalInput
            label="Address"
            name="address"
            placeholder="Enter address"
            value={formData.address}
            onChange={handleInputChange}
            required
            error={errors.address}
          />

          <Dropdown
            label="Country"
            name="country"
            options={mapDataToLabelValue(countries, "name", "id")}
            value={formData.country}
            onSelect={(value) => handleSelectChange("country", value)}
            error={errors.country}
            placeholder="Select country"
          />
          <Dropdown
            label="Offices"
            name="offices"
            options={mapDataToLabelValue(
              courierOffices?.data?.data,
              "name",
              "id"
            )}
            required={true}
            value={formData.offices}
            onSelect={(value) => handleSelectChange("offices", value)}
            error={errors.offices}
            placeholder="Select office"
          />

          <ModalInput
            label="Username"
            name="username"
            placeholder="Enter username"
            value={formData.username}
            onChange={handleInputChange}
            required
            error={errors.username}
          />

          <ModalInput
            label="Password"
            name="password"
            type="password"
            placeholder="Enter password"
            value={formData.password}
            onChange={handleInputChange}
            required
            error={errors.password}
          />

          <ModalInput
            label="Confirm Password"
            name="confirmPassword"
            type="password"
            placeholder="Confirm password"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            required
            error={errors.confirmPassword}
          />
        </form>
      </div>
    </CustomModalContainer>
  );
};

export default AddAgentModal;
